import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  ButtonGroup,
  DonutChart,
  DateRangePicker,
  defaultDateRangePresets,
  type ButtonGroupOption,
  type DateRange,
  type DonutChartSegment,
} from "@act-group/act-ui-library";
import { VerificationsGrid } from "../../../components/VerificationsGrid";
import { useVerifications } from "../../../hooks/useVerifications";
import { useVerificationsSummary } from "../../../hooks/useVerificationsSummary";
import { formatDeclineReasonLabel } from "../../../utils/verificationLabels";
import { toDayjsLocale } from "../../../constants/i18n";
import {
  isTemporarilyHiddenVerification,
  isUntouchedVerification,
} from "../../../types/verification";

// Cycled by index across however many distinct decline reasons the API returns.
const DECLINE_REASON_COLORS = [
  "#b71c1c",
  "#ef6c00",
  "#ad1457",
  "#d32f2f",
  "#e64a19",
  "#f4511e",
  "#ff7043",
  "#ffab91",
];

// Cycled by index across however many distinct OS values the API returns.
const DEVICE_OS_COLORS = [
  "#6a1b9a",
  "#1565c0",
  "#00838f",
  "#2e7d32",
  "#9e9d24",
  "#ef6c00",
  "#8e24aa",
  "#5c6bc0",
];

// Matches DonutChart's own card sizing/spacing so it sits flush alongside real
// charts in the grid. Used in place of DonutChart when there's no data yet —
// an empty `segments` array makes MUI X's built-in "No data to display"
// overlay collide with DonutChart's own center "Total" label.
function EmptyDonutCard({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        padding: "0.625rem 1.25rem 1.25rem 1.25rem",
        minHeight: "220px",
        maxHeight: "220px",
        borderRadius: "0.25rem",
        bgcolor: "background.paper",
        minWidth: 320,
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{ mb: "0.5rem", textDecoration: "underline" }}
      >
        {title}
      </Typography>
      <Stack alignItems="center" justifyContent="center" sx={{ height: "140px" }}>
        <Typography variant="body2" color="text.secondary">
          {t("No data available")}
        </Typography>
      </Stack>
    </Box>
  );
}

const DATE_PARAM_FORMAT = "YYYY-MM-DD";

const DEFAULT_DATE_RANGE = (
  defaultDateRangePresets.find((preset) => preset.id === "last-30-days") ??
  defaultDateRangePresets[0]
).getRange();

// Reads the jurisdiction/date-range filters back out of the URL on mount, so
// they survive navigating to a verification's detail page and back — without
// this, both reset to their defaults on every remount since they'd otherwise
// live in plain component state, and the route unmounts this page entirely
// while the detail page is shown.
function readJurisdictionParam(searchParams: URLSearchParams): string | undefined {
  return searchParams.get("jurisdiction") ?? undefined;
}

function readDateRangeParam(searchParams: URLSearchParams): DateRange {
  const from = searchParams.get("fromDate");
  const to = searchParams.get("toDate");
  if (!from || !to) return DEFAULT_DATE_RANGE;
  const start = dayjs(from, DATE_PARAM_FORMAT, true);
  const end = dayjs(to, DATE_PARAM_FORMAT, true);
  if (!start.isValid() || !end.isValid()) return DEFAULT_DATE_RANGE;
  return { start, end };
}

function DashboardPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Empty until the summary's availableJurisdictions come back — the first
  // request is deliberately unscoped, then the first jurisdiction is selected
  // (unless the URL already named one, e.g. returning from a detail page).
  const [jurisdiction, setJurisdictionState] = useState<string | undefined>(() =>
    readJurisdictionParam(searchParams)
  );
  const [dateRange, setDateRangeState] = useState<DateRange>(() =>
    readDateRangeParam(searchParams)
  );

  // Wrap the setters so every change to either filter also updates the URL —
  // `replace: true` keeps the back button from stepping through every
  // intermediate filter change instead of leaving the page. useCallback gives
  // each a stable identity so the auto-jurisdiction-selection effect below can
  // depend on setJurisdiction without re-running on every render.
  const setJurisdiction = useCallback(
    (next: string) => {
      setJurisdictionState(next);
      setSearchParams(
        (params) => {
          params.set("jurisdiction", next);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setDateRange = useCallback(
    (next: DateRange) => {
      setDateRangeState(next);
      setSearchParams(
        (params) => {
          params.set("fromDate", next.start.format(DATE_PARAM_FORMAT));
          params.set("toDate", next.end.format(DATE_PARAM_FORMAT));
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Translate the preset labels (their ids/ranges stay stable) and the picker's
  // own chrome, so the date picker follows the UI language.
  const datePresets = useMemo(
    () => defaultDateRangePresets.map((preset) => ({ ...preset, label: t(preset.label) })),
    [t]
  );
  const datePickerLabels = useMemo(
    () => ({
      placeholder: t("Select date range"),
      apply: t("Apply"),
      cancel: t("Cancel"),
      custom: t("Custom"),
      startDate: t("Start Date"),
      endDate: t("End Date"),
    }),
    [t]
  );

  // One jurisdiction drives both the summary cards and the verifications grid,
  // so the whole dashboard always shows the same slice of data.
  const {
    activity,
    devices,
    declineReasons,
    isLoading: isSummaryLoading,
  } = useVerificationsSummary({
    dateRange,
    jurisdiction,
  });

  // The tab options themselves always come from a *separate, unfiltered* call
  // — a jurisdiction-scoped summary may legitimately narrow its own
  // availableJurisdictions to just the selected one, so deriving the tabs from
  // the call above would make them vanish whenever a jurisdiction is already
  // selected on mount (e.g. restored from the URL after returning from a
  // detail page, rather than starting unfiltered like a fresh page load does).
  const { availableJurisdictions, hasResolved: summaryHasResolved } = useVerificationsSummary({
    dateRange,
  });

  // Don't fetch the verifications list until we know which jurisdiction (if
  // any) to scope it to — otherwise it fetches once unfiltered, renders every
  // jurisdiction's rows, then immediately refetches and swaps to the filtered
  // set as soon as the summary resolves the default. Ready once either a
  // jurisdiction has been picked, or the summary has resolved and confirmed
  // there are none to filter by.
  const listReady =
    Boolean(jurisdiction) || (summaryHasResolved && availableJurisdictions.length === 0);

  const { rows, rowCount, isLoading, error, paginationModel, setPaginationModel } =
    useVerifications({ dateRange, jurisdiction, ready: listReady });

  // Hide untouched verifications (see isUntouchedVerification) — nothing has
  // happened to them yet, so listing them adds noise without information.
  // Also applies a TEMPORARY name-based hide (isTemporarilyHiddenVerification)
  // — see that function's comment for why/how to remove it.
  const dashboardRows = useMemo(
    () =>
      rows.filter((row) => !isUntouchedVerification(row) && !isTemporarilyHiddenVerification(row)),
    [rows]
  );

  // The tab's options are API-driven (no separate jurisdictions endpoint), so
  // the initial selection can only be made once the first summary lands. Also
  // recovers if the selected jurisdiction disappears from the list. Skips the
  // update (and the URL write it'd trigger) when the current selection — e.g.
  // one just restored from the URL — is still valid, so returning from a
  // detail page doesn't get silently reset to the first jurisdiction.
  useEffect(() => {
    if (availableJurisdictions.length === 0) return;
    if (jurisdiction && availableJurisdictions.includes(jurisdiction)) return;
    setJurisdiction(availableJurisdictions[0]);
  }, [availableJurisdictions, jurisdiction, setJurisdiction]);

  const jurisdictionOptions = useMemo<ButtonGroupOption[]>(
    // Names arrive from the API ("Nevada", "Brasil", ...); t() picks up a
    // translation where one exists and falls back to the name itself otherwise.
    () => availableJurisdictions.map((name) => ({ id: name, label: t(name) })),
    [availableJurisdictions, t]
  );

  const activitySegments = useMemo<DonutChartSegment[]>(() => {
    if (!activity || activity.total === 0) return [];
    return [
      { id: "passed", label: t("Passed"), value: activity.passed, color: "#2e7d32" },
      { id: "failed", label: t("Failed"), value: activity.failed, color: "#c62828" },
      { id: "pending", label: t("Pending"), value: activity.pending, color: "#f9a825" },
    ];
  }, [activity, t]);

  const deviceSegments = useMemo<DonutChartSegment[]>(() => {
    if (!devices || devices.byOs.breakdown.length === 0) return [];
    return [...devices.byOs.breakdown]
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({
        id: item.id,
        label: item.label,
        value: item.value,
        color: DEVICE_OS_COLORS[index % DEVICE_OS_COLORS.length],
      }));
  }, [devices]);

  const declineReasonSegments = useMemo<DonutChartSegment[]>(() => {
    if (!declineReasons || declineReasons.items.length === 0) return [];
    return [...declineReasons.items]
      .sort((a, b) => b.count - a.count)
      .map((item, index) => ({
        id: item.reason,
        label: t(formatDeclineReasonLabel(item.reason)),
        value: item.count,
        color: DECLINE_REASON_COLORS[index % DECLINE_REASON_COLORS.length],
      }));
  }, [declineReasons, t]);

  return (
    <Stack spacing={3}>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>
        {t("Dashboard")}
      </Typography>

      <Stack
        direction="row"
        // Until the jurisdictions arrive the picker is the row's only child, and
        // space-between has no effect on a single item — so pin it right instead.
        justifyContent={jurisdictionOptions.length > 0 ? "space-between" : "flex-end"}
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        {/* Rendered only once the summary has supplied real jurisdictions —
            an empty ButtonGroup would otherwise flash as a bare pill outline. */}
        {jurisdictionOptions.length > 0 && (
          <ButtonGroup
            options={jurisdictionOptions}
            value={jurisdiction ?? ""}
            onChange={setJurisdiction}
          />
        )}
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          presets={datePresets}
          labels={datePickerLabels}
          adapterLocale={toDayjsLocale(i18n.language)}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 3,
          "& > div": { height: "100%", minWidth: 0 },
        }}
      >
        {!isSummaryLoading && activitySegments.length === 0 ? (
          <EmptyDonutCard title={t("Activity")} />
        ) : (
          <DonutChart
            title={t("Activity")}
            segments={activitySegments}
            loading={isSummaryLoading}
          />
        )}
        {!isSummaryLoading && deviceSegments.length === 0 ? (
          <EmptyDonutCard title={t("Devices")} />
        ) : (
          <DonutChart
            title={t("Devices")}
            segments={deviceSegments}
            maxLegendRows={3}
            showMoreLabel={t("show more...")}
            loading={isSummaryLoading}
          />
        )}
        {!isSummaryLoading && declineReasonSegments.length === 0 ? (
          <EmptyDonutCard title={t("Reasons for Decline")} />
        ) : (
          <DonutChart
            title={t("Reasons for Decline")}
            segments={declineReasonSegments}
            maxLegendRows={3}
            showMoreLabel={t("show more...")}
            loading={isSummaryLoading}
          />
        )}
      </Box>

      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          {t("Verifications")}
        </Typography>
        <VerificationsGrid
          rows={dashboardRows}
          rowCount={rowCount}
          isLoading={isLoading}
          error={error}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          jurisdiction={jurisdiction}
        />
      </Stack>
    </Stack>
  );
}

export default DashboardPage;
